import { ActionIcon, Checkbox, Loader, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconGps, IconX } from "@tabler/icons-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import { runWithSavedStateMarkingSuspended } from "src/generator/hooks/savedStateGuard";
import { useTriggerAutoSave } from "src/generator/hooks/useTriggerAutoSave";
import { unregisterRowFormFields } from "src/generator/utils/collectActiveFormFieldNames";
import {
  useGetApi,
  useIsNewEntry,
  useIsOnline,
  usePostApi,
  useTheme,
} from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setNMEAModalData } from "src/redux/slices/generatorFeaturesSlice";
import {
  addNewDynamicRow,
  setNMEAData,
  setSections,
} from "src/redux/slices/generatorSlice";
import { insertValues } from "src/redux/utils/insertValues";
import { Flex } from "src/styled";
import { crumb, getYachtId } from "src/utils";

import NMEAAutomationModalPreview from "./NMEAAoutomationModalPreview";
import NMEAAoutomationModalPreviewLegacy from "./NMEAAoutomationModalPreviewLegacy";
import {
  applyEntryDateToNMEARows,
  applyShipsTimeOffsetToNMEARows,
  cleanupInvalidNMEASelections,
  deriveNMEAId,
  getOnlyNMEARows,
  getRowNMEAId,
  type INMEARow,
  type INMEASection,
  isNMEARowMatchedInSection,
  parseNMEADataForSaving,
  parseNMEASections,
  parseShipsTimeOffset,
} from "./NMEAAutomationModal.utils";
import {
  isDagonNMEATimezoneOverrideLogbook,
  isLegacyNMEAPreviewLogbook,
} from "./nmeaPreviewStyling";
import NMEAShipsTimeOffsetInput from "./NMEAShipsTimeOffsetInput";
import { useNMEALocation } from "./useNMEALocation";
import { NMEAPreviewCheckboxNavigationProvider } from "./useNMEAPreviewCheckboxNavigation";
import { useSyncEntryShipsTimeOffset } from "./useSyncEntryShipsTimeOffset";

const mapper = (data: any): Array<Record<string, string>> => data.data;

const NMEAAutomationModal = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [opened, { open, close }] = useDisclosure();
  const { setValue, getValues, unregister } = useFormContext();
  const triggerAutoSave = useTriggerAutoSave();
  const { lbeidOrPartId, logbookId } = useParams();
  const { NMEAModalData } = useSelector(s => s.generatorFeaturesSlice);
  const { lbentry, entryDataToSave, sortOrder } = useSelector(
    s => s.generatorSlice,
  );
  const useLegacyPreview = isLegacyNMEAPreviewLogbook(logbookId);
  const isDagonTimezoneOverrideLogbook =
    isDagonNMEATimezoneOverrideLogbook(logbookId);
  const shipTimeFieldIds = useSyncEntryShipsTimeOffset(
    isDagonTimezoneOverrideLogbook,
  );
  const isNewEntry = useIsNewEntry();
  const { post } = usePostApi();
  const isOnline = useIsOnline();
  const [selectedSections, setSelectedSections] = useState<number[][]>([]);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const {
    anchorChecked,
    underwayChecked,
    location,
    setAnchorChecked,
    setUnderwayChecked,
  } = useNMEALocation("underway");

  // Helper function to check if an NMEA row still corresponds to an existing logbook row in given sections
  const isNMEARowStillValidForSections = (
    nmeaRow: INMEARow,
    sectionIndex: number,
    sections: typeof lbentry.sections,
  ): boolean => {
    const section = sections[sectionIndex];
    if (!section) return false;

    return isNMEARowMatchedInSection(nmeaRow, section);
  };

  // Track initial location to detect changes
  const initialLocationRef = useRef<string | null>(null);
  // Capture and show the value of entryDataToSave?.latest === id only when id is first defined (not undefined/null/empty)
  const isSophia = getYachtId() === "a810afbe-3e95-5729-1e8d-1bb8306fd3f0";

  // Wait until id is not undefined before constructing nmeaApiBody
  const nmeaApiBody = useMemo(() => {
    if (id === undefined || id === null || String(id).length === 0)
      return undefined;
    return {
      params: {
        yachtid: getYachtId(),
        lbid: logbookId,
      },
    };
  }, [logbookId, id]);

  const { data, refetch, isLoading, setData } = useGetApi({
    request: "get-NMEA-data",
    body: nmeaApiBody,
    mapper,
    fetchOnInit: false,
  });

  const effectiveShipsTimeOffset = useMemo(
    () =>
      isDagonTimezoneOverrideLogbook
        ? parseShipsTimeOffset(lbentry.shipsTime)
        : null,
    [isDagonTimezoneOverrideLogbook, lbentry.shipsTime],
  );

  const effectiveData = useMemo(() => {
    if (!data?.length) return [];

    let result = data;

    if (isDagonTimezoneOverrideLogbook) {
      result = applyShipsTimeOffsetToNMEARows(result, effectiveShipsTimeOffset);
    }

    if (isSophia) {
      result = applyEntryDateToNMEARows(result, entryDataToSave?.entrydate);
    }

    return result;
  }, [
    data,
    effectiveShipsTimeOffset,
    entryDataToSave?.entrydate,
    isDagonTimezoneOverrideLogbook,
    isSophia,
  ]);

  useEffect(() => {
    if (
      id === undefined ||
      id === null ||
      String(id).length === 0 ||
      isLoading ||
      !isOnline ||
      !NMEAModalData?.opened ||
      !entryDataToSave?.latest
    ) {
      return;
    }
    if (lbentry.NMEA) {
      if (!lbentry.NMEAData?.length || NMEAModalData?.triggeredBy) {
        const params = {
          ...(NMEAModalData.rowIndex !== undefined && {
            date: entryDataToSave.entrydate,
          }),
          ...(!NMEAModalData?.triggeredBy && { newEntry: true }),
        };

        refetch({
          params,
        }).catch(error => {
          console.error("Error fetching NMEA data:", error);
        });
      } else {
        // Clean up any invalid selections before processing
        const cleanedNMEAData = cleanupInvalidNMEASelections(
          lbentry.NMEAData,
          lbentry.sections,
        );

        // Initialize selectedSections array with proper indices based on originalSectionIndex
        const newSelectedSections: number[][] = [];

        const flatData: Array<Record<string, string>> = (
          cleanedNMEAData ?? []
        ).flatMap((section: INMEASection) =>
          section.rows.flatMap((r, index): any => {
            if (!r) return [];
            // Use the cleaned selection state
            const shouldBeSelected =
              (r.selected ?? false) &&
              isNMEARowStillValidForSections(
                r,
                section.originalSectionIndex,
                lbentry.sections,
              );

            const newRow: Record<string, string | number | boolean> = {
              _originalSectionIndex: section.originalSectionIndex,
              _NMEAId: deriveNMEAId({
                NMEAId: r.NMEAId,
                _ROW_DATE_TIME_LT: r.dateTimeLT,
              }),
              _wasSelected: shouldBeSelected,
            };

            if (shouldBeSelected) {
              // Initialize the array for this section if it doesn't exist
              if (!newSelectedSections[section.originalSectionIndex]) {
                newSelectedSections[section.originalSectionIndex] = [];
              }
              newSelectedSections[section.originalSectionIndex] = [
                ...newSelectedSections[section.originalSectionIndex],
                index,
              ];
            }

            let hasTimeCol = false;
            Object.entries(r.cols).forEach(([key, col]) => {
              const nmeaKey = col.NMEA ?? key;
              if (nmeaKey === "ROW_TIME" || nmeaKey === "_ROW_DATE_TIME") {
                newRow[nmeaKey] =
                  col.NMEAValue ??
                  (typeof col.value === "object" &&
                  col.value !== null &&
                  "time" in col.value
                    ? (col.value as { time?: string }).time
                    : col.value);
                hasTimeCol = true;
              } else if (col.NMEA) {
                newRow[col.NMEA] = col.value as string;
              }
            });

            const dateTimeObj = {
              _ROW_DATE_TIME: r.dateTime ?? "",
              _ROW_DATE_TIME_UTC: r.dateTimeUTC ?? "",
              _ROW_DATE_TIME_LT: r.dateTimeLT ?? "",
              _ROW_TIME_UTC: r.timeUTC ?? "",
              _ROW_TIME_LT: r.timeLT ?? "",
            };

            const hasRowTime = Boolean(
              r.timeLT || r.cols?.ROW_TIME || r.cols?._ROW_DATE_TIME,
            );

            return {
              ...newRow,
              ...(hasTimeCol || hasRowTime ? dateTimeObj : {}),
            };
          }),
        );

        setData?.(() => {
          return flatData;
        });
        setSelectedSections(newSelectedSections);
      }
      open();
    } else {
      if (NMEAModalData?.opened) {
        dispatch(setNMEAModalData(null));
      }
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [
    lbentry,
    NMEAModalData,
    entryDataToSave,
    isOnline,
    id,
    close,
    open,
    refetch,
    setData,
  ]);

  // Capture initial location when modal opens
  useEffect(() => {
    if (opened && initialLocationRef.current === null) {
      initialLocationRef.current = location;
    }
    if (!opened) {
      initialLocationRef.current = null;
    }
  }, [opened, location]);

  const handleClose = (
    dataWasAccepted: boolean = false,
    updatedSections?: typeof lbentry.sections,
  ) => {
    // Check for location changes and record history
    const currentLocation = location;
    const initialLocation = initialLocationRef.current;

    if (
      initialLocation &&
      currentLocation &&
      initialLocation !== currentLocation
    ) {
      recordHistory(`Location change to ${currentLocation}`).catch(error =>
        console.error(error),
      );
    }

    recordNMEAData(dataWasAccepted, updatedSections);
    dispatch(setNMEAModalData(null));
    close();
    setSelectedSections([]);
    // dispatch(sortSectionsByTime(NMEAModalData?.sectionIndex));
  };

  useEffect(() => {
    return () => {
      dispatch(setNMEAModalData(null));
      close();
    };
  }, [close, dispatch]);

  const recordHistory = async (action: string) => {
    if (!isOnline) return;
    await post({
      request: "record-history-action",
      body: {
        params: {
          yachtid: getYachtId(),
          lbid: logbookId,
          lbeid: lbeidOrPartId,
          action,
        },
      },
      shouldRunAfterAutosave: true,
    });
  };

  const findSelectedSections = () => {
    const filteredData: INMEASection[] = [];
    previewSections.forEach(s => {
      let newRows: INMEARow[] = [];
      newRows = s.rows.map((r, rowIdx) => ({
        ...r,
        selected: selectedSections[s.originalSectionIndex]?.includes(rowIdx),
      }));

      filteredData.push({
        ...s,
        rows: newRows,
      });
    });
    return filteredData;
  };

  const recordNMEAData = (
    dataWasAccepted?: boolean,
    updatedSections?: typeof lbentry.sections,
  ) => {
    if (!dataWasAccepted) return;

    const sectionsWithSelectedProp = findSelectedSections();

    // Use the updated sections if provided, otherwise use the current sections
    const sectionsToUse = updatedSections ?? lbentry.sections;

    // Clean up invalid selections from existing NMEA data first
    const cleanedExistingNMEAData = cleanupInvalidNMEASelections(
      lbentry.NMEAData,
      sectionsToUse,
    );
    // Clean up invalid selections from current selections
    const cleanedSectionsWithSelectedProp = sectionsWithSelectedProp.map(
      section => ({
        ...section,
        rows: section.rows.map(row => ({
          ...row,
          selected: row.selected ?? false,
        })),
      }),
    );

    const newNMEAData = parseNMEADataForSaving({
      sections: sectionsToUse,
      sectionsWithSelectedProp: cleanedSectionsWithSelectedProp,
      NMEAData: cleanedExistingNMEAData,
      NMEAModalData,
    });

    dispatch(setNMEAData(newNMEAData));
  };

  const handleAccept = async () => {
    const startTime = Date.now();
    const shouldDropPlaceholderRows = isNewEntry && !NMEAModalData?.triggeredBy;

    // Log NMEA automation acceptance
    crumb("NMEA Modal: Automated data accepted", {
      triggeredBy: NMEAModalData?.triggeredBy,
      newEntry: isNewEntry,
      entryId: lbeidOrPartId ?? id,
      logbookId,
      sectionIndex: NMEAModalData?.sectionIndex,
      rowIndex: NMEAModalData?.rowIndex,
      selectedSectionsCount: findSelectedSections().length,
    });

    await recordHistory("Automated Data Accepted");
    const filledSections = insertValues(getValues(), lbentry.sections);
    const newSections = parseNMEASections({
      originalSections: filledSections,
      newData: effectiveData,
      setValue,
      rowIndex: NMEAModalData?.rowIndex,
      sectionIndex: NMEAModalData?.sectionIndex,
      selectedSections: findSelectedSections(),
      sortOrder,
      dropRowsWithoutNMEAId: shouldDropPlaceholderRows,
      removeUnselectedNMEARows: useLegacyPreview,
      ...(isSophia && { location }),
    });

    if (useLegacyPreview) {
      filledSections.forEach((section, sectionIdx) => {
        const newSection = newSections[sectionIdx];
        if (!section?.rows || !newSection?.rows) return;

        const retainedNmeaIds = new Set(
          newSection.rows
            .map(getRowNMEAId)
            .filter((id): id is string => Boolean(id)),
        );

        section.rows.forEach(row => {
          const nmeaId = getRowNMEAId(row);
          if (nmeaId && !retainedNmeaIds.has(nmeaId)) {
            unregisterRowFormFields(getValues(), row, unregister);
          }
        });
      });
    }

    const duration = Date.now() - startTime;
    crumb("NMEA Modal: Data processing completed", {
      hourlyRowsCountBeforeNMEA: filledSections[1]?.rows?.length,
      hourlyRowsCountAfterNMEA: newSections[1]?.rows?.length,
      duration,
      sectionsProcessed: newSections.length,
      success: true,
    });

    runWithSavedStateMarkingSuspended(() => {
      dispatch(setSections(newSections));
      handleClose(true, newSections);
      triggerAutoSave();
    });
  };

  const handleEnterManually = async () => {
    runWithSavedStateMarkingSuspended(() => {
      // Add the prepared row for manual entry
      if (
        NMEAModalData?.pendingRow &&
        NMEAModalData?.sectionIndex !== undefined
      ) {
        dispatch(
          addNewDynamicRow({
            row: NMEAModalData.pendingRow,
            sectionIndex: NMEAModalData.sectionIndex,
            skipSorting:
              lbentry.sections[NMEAModalData.sectionIndex]?.NMEA_button,
            withTime: false,
          }),
        );
      }

      handleClose(true);
      triggerAutoSave();
    });
    void recordHistory("Data Entered Manually");
  };

  const previewSections: INMEASection[] = useMemo(() => {
    if (!effectiveData.length || !opened) return [];
    const filledSections = parseNMEASections({
      originalSections: lbentry.sections,
      newData: effectiveData,
      rowIndex: NMEAModalData?.rowIndex,
      sectionIndex: NMEAModalData?.sectionIndex,
      sortOrder,
    });
    // For add mode, check against original lbentry.sections to properly filter existing rows
    const sectionsForExistenceCheck =
      NMEAModalData?.triggeredBy === "add" ? lbentry.sections : filledSections;
    const NMEARows = getOnlyNMEARows(
      sectionsForExistenceCheck,
      effectiveData,
      NMEAModalData?.sectionIndex,
      NMEAModalData?.triggeredBy,
    );
    return NMEARows;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [
    effectiveData,
    effectiveShipsTimeOffset,
    lbentry.sections,
    NMEAModalData,
    opened,
    sortOrder,
  ]);

  return (
    <Modal
      opened={opened}
      onClose={() => handleClose(true)}
      centered={false}
      yOffset={50}
      size={useLegacyPreview ? "60%" : "80%"}
      zIndex={10}
      withCloseButton={false}
      radius={7}
      overlayProps={{ color: "#000", opacity: 0.5 }}
      styles={{
        content: {
          backgroundColor: theme.colors.background[0],
          border: `2px solid ${theme.colors.inputBorder[0]}`,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          maxHeight: "calc(100vh - 100px)",
        },
        body: {
          padding: 18,
          paddingTop: 10,
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 105px)",
        },
      }}>
      <Flex direction="column" gap={10} style={{ flex: 1, minHeight: 0 }}>
        <Flex justify="space-between">
          <Text size="xl" c={theme.colors.text[0]}>
            Data Automation
          </Text>
          <ActionIcon onClick={() => handleClose()}>
            <IconX />
          </ActionIcon>
        </Flex>
        {isDagonTimezoneOverrideLogbook && (
          <NMEAShipsTimeOffsetInput shipTimeFieldIds={shipTimeFieldIds} />
        )}
        <Flex gap={8} justify="flex-start">
          <Flex direction="column" w={isSophia ? "70%" : "100%"}>
            <Text fz={15}>
              Please review and confirm that all auto-filled information is
              correct.
            </Text>
            <Text fz={15}>
              By clicking "<span style={{ fontWeight: 200 }}>Accept</span>", you
              agree to the use of automated inputs and take responsibility for
              verifying their accuracy.
            </Text>
          </Flex>
          {isSophia && (
            <Flex direction="column" gap={8} w={"20%"} justify="center">
              <Flex gap={8} justify="flex-end" align="center">
                <Text fz={15}>Anchor</Text>
                <Checkbox
                  icon={IconGps}
                  size={"lg"}
                  checked={anchorChecked}
                  onChange={event =>
                    setAnchorChecked(event.currentTarget.checked)
                  }
                />
              </Flex>
              <Flex gap={8} justify="flex-end" align="center">
                <Text fz={15}>Underway</Text>
                <Checkbox
                  icon={IconGps}
                  size={"lg"}
                  checked={underwayChecked}
                  onChange={event =>
                    setUnderwayChecked(event.currentTarget.checked)
                  }
                />
              </Flex>
            </Flex>
          )}
        </Flex>
        <Flex
          justify="center"
          direction="column"
          align="center"
          mt={10}
          style={{ flex: 1, minHeight: 0 }}>
          {isLoading ? (
            <Loader />
          ) : (
            <NMEAPreviewCheckboxNavigationProvider>
              <Flex
                direction="column"
                style={{ flex: 1, minHeight: 0, width: "100%" }}>
                <Flex
                  direction="column"
                  justify="flex-start"
                  gap={20}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "auto",
                    marginLeft: 4,
                    paddingRight: 10,
                  }}>
                  {previewSections.map(
                    (s, i) =>
                      s.rows.length > 0 && (
                        <Fragment key={`pr-sec-${i}`}>
                          {useLegacyPreview ? (
                            <NMEAAoutomationModalPreviewLegacy
                              section={s}
                              sectionIndex={s.originalSectionIndex}
                              selectedSections={selectedSections}
                              setSelectedSections={setSelectedSections}
                              onlyOneAllowed={false}
                            />
                          ) : (
                            <NMEAAutomationModalPreview
                              section={s}
                              sectionIndex={s.originalSectionIndex}
                              sectionName={
                                lbentry.sections[s.originalSectionIndex]?.title
                              }
                              selectedSections={selectedSections}
                              setSelectedSections={setSelectedSections}
                              onlyOneAllowed={false}
                            />
                          )}
                        </Fragment>
                      ),
                  )}
                </Flex>
              </Flex>
            </NMEAPreviewCheckboxNavigationProvider>
          )}
        </Flex>

        <Flex justify="space-between" mt={10}>
          <Button onClick={handleEnterManually}>Enter Manually</Button>
          <Button
            onClick={handleAccept}
            disabled={
              selectedSections.length === 0 ||
              selectedSections.every(s => !s || s.length === 0)
            }>
            Accept
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default NMEAAutomationModal;
